import { useEffect, useState, useCallback } from 'react';
import { repGet, repPost, repPatch, repDelete, repGetBlob } from './reportsApi';
import { adminGet } from '../adminApi';
import { downloadBlob } from '../adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Card, GREEN, mono } from '../bfsi/bfsiShared';

export function ExportBar({ type, filters, onJobCreated }) {
  const [busy, setBusy] = useState(null);
  const doExport = async (fmt) => {
    setBusy(fmt);
    try {
      const job = await repPost('/exports', { type, filters, format: fmt });
      if (job.status === 'AWAITING_APPROVAL') {
        toast.info('BFSI raw data export — an approval request was created (see Approvals)');
      } else {
        toast.success(`${fmt} export started`);
      }
      onJobCreated?.(job);
    } catch (e) { toast.error(e.message); }
    setBusy(null);
  };
  return (
    <div className="flex gap-2">
      {['XLSX', 'PDF', 'CSV'].map(f => (
        <Button key={f} size="sm" variant="outline" disabled={busy === f} data-testid={`export-${f.toLowerCase()}-btn`}
          onClick={() => doExport(f)} className="bg-white border-[#D9D6CD] h-8 text-xs">{f}</Button>
      ))}
    </div>
  );
}

export function ExportsPanel({ refreshKey }) {
  const [jobs, setJobs] = useState([]);
  const load = useCallback(() => { repGet('/exports').then(setJobs).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    if (!jobs.some(j => ['QUEUED', 'RUNNING', 'AWAITING_APPROVAL'].includes(j.status))) return;
    const t = setTimeout(async () => {
      for (const j of jobs.filter(x => ['QUEUED', 'RUNNING', 'AWAITING_APPROVAL'].includes(x.status))) {
        await repGet(`/jobs/${j.id}`).catch(() => {});
      }
      load();
    }, 2500);
    return () => clearTimeout(t);
  }, [jobs, load]);
  const download = async (j) => {
    try {
      const blob = await repGetBlob(`/jobs/${j.id}/download`);
      downloadBlob(blob, `${j.type.toLowerCase()}-report.${j.format.toLowerCase()}`);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Card className="p-0 overflow-x-auto" data-testid="exports-panel">
      <div className="px-4 pt-4 pb-2 font-bold text-sm" style={{ color: '#1B1D1C' }}>Recent exports</div>
      <table className="w-full text-sm">
        <tbody>
          {jobs.length === 0 && <tr><td className="px-4 py-4 text-center" style={{ color: '#9a9e96' }}>No exports yet</td></tr>}
          {jobs.slice(0, 10).map(j => (
            <tr key={j.id} className="border-t" style={{ borderColor: '#F0EEE8' }}>
              <td className="px-4 py-2" style={mono}>{j.type}</td>
              <td className="px-2 py-2 text-xs">{j.format}</td>
              <td className="px-2 py-2">
                <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={j.status === 'DONE' ? { background: '#E3F1EC', color: GREEN }
                    : j.status === 'FAILED' ? { background: '#FBEAE5', color: '#B3401F' }
                      : { background: '#FDF3E0', color: '#9a6b12' }}>
                  {j.status}{['QUEUED', 'RUNNING'].includes(j.status) ? ` ${j.progress_pct}%` : ''}
                </span>
              </td>
              <td className="px-2 py-2 text-right pr-4">
                {j.status === 'DONE' && (
                  <button className="text-xs hover:underline" style={{ color: GREEN }}
                    data-testid={`download-job-${j.id}`} onClick={() => download(j)}>Download</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export function SchedulesPanel({ type, filters, tenantId }) {
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ frequency: 'WEEKLY', send_time: '08:00', weekday: 1, format: 'XLSX', recipient: '' });
  const [runs, setRuns] = useState(null);

  const load = useCallback(() => {
    repGet(`/schedules${tenantId ? `?tenant_id=${tenantId}` : ''}`).then(setSchedules).catch(() => {});
    if (tenantId) adminGet(`/customers/${tenantId}/users`).then(u => setUsers(u.users || u)).catch(() => setUsers([]));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await repPost('/schedules', {
        type, filters, format: form.format, frequency: form.frequency,
        send_time: form.send_time, weekday: form.frequency === 'WEEKLY' ? Number(form.weekday) : null,
        recipient_user_ids: form.recipient ? [form.recipient] : [],
      });
      toast.success('Schedule created'); setShow(false); load();
    } catch (e) { toast.error(e.message); }
  };
  const runNow = async (s) => {
    try {
      const r = await repPost(`/schedules/${s.id}/run-now`);
      toast.success(`Run ${r.status}: sent ${r.sent_to.length}, skipped ${r.skipped.length}`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  const toggle = async (s) => {
    try { await repPatch(`/schedules/${s.id}`, { active: !s.active }); load(); } catch (e) { toast.error(e.message); }
  };
  const remove = async (s) => {
    try { await repDelete(`/schedules/${s.id}`); toast.success('Schedule deleted'); load(); } catch (e) { toast.error(e.message); }
  };
  const showRuns = async (s) => {
    try { setRuns({ id: s.id, list: await repGet(`/schedules/${s.id}/runs`) }); } catch (e) { toast.error(e.message); }
  };

  return (
    <Card data-testid="schedules-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="font-bold text-sm" style={{ color: '#1B1D1C' }}>Scheduled reports</div>
        <Button size="sm" data-testid="schedule-new-btn" onClick={() => setShow(!show)}
          className="h-8 text-xs text-white" style={{ background: GREEN }}>Schedule this report</Button>
      </div>
      {show && (
        <div className="rounded-lg border p-3 mb-3 grid grid-cols-2 gap-3" style={{ borderColor: '#E5E3DC' }}>
          <div>
            <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>Frequency</label>
            <select data-testid="schedule-frequency" value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value })}
              className="w-full h-8 rounded-md border px-2 text-sm bg-white" style={{ borderColor: '#D9D6CD' }}>
              <option>DAILY</option><option>WEEKLY</option><option>MONTHLY</option>
            </select>
          </div>
          <div>
            <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>Send time (tenant tz)</label>
            <Input data-testid="schedule-time" type="time" value={form.send_time} onChange={e => setForm({ ...form, send_time: e.target.value })} className="h-8" />
          </div>
          <div>
            <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>Format</label>
            <select data-testid="schedule-format" value={form.format} onChange={e => setForm({ ...form, format: e.target.value })}
              className="w-full h-8 rounded-md border px-2 text-sm bg-white" style={{ borderColor: '#D9D6CD' }}>
              <option>XLSX</option><option>PDF</option><option>CSV</option>
            </select>
          </div>
          <div>
            <label className="text-xs block mb-1" style={{ color: '#4b504a' }}>Recipient (same tenant)</label>
            <select data-testid="schedule-recipient" value={form.recipient} onChange={e => setForm({ ...form, recipient: e.target.value })}
              className="w-full h-8 rounded-md border px-2 text-sm bg-white" style={{ borderColor: '#D9D6CD' }}>
              <option value="">— pick a user —</option>
              {(users || []).map(u => <option key={u.id} value={u.id}>{u.email || u.name}</option>)}
            </select>
          </div>
          <Button size="sm" data-testid="schedule-create-btn" onClick={create} disabled={!form.recipient}
            className="h-8 text-xs text-white col-span-2" style={{ background: GREEN }}>Create schedule</Button>
        </div>
      )}
      <table className="w-full text-sm">
        <tbody>
          {schedules.length === 0 && <tr><td className="py-3 text-center" style={{ color: '#9a9e96' }}>No schedules</td></tr>}
          {schedules.map(s => (
            <tr key={s.id} className="border-t" style={{ borderColor: '#F0EEE8' }} data-testid={`schedule-row-${s.id}`}>
              <td className="py-2" style={mono}>{s.type}</td>
              <td className="py-2 text-xs">{s.frequency} {s.send_time} · {s.format}</td>
              <td className="py-2 text-xs">{(s.recipient_user_ids || []).length} recipient(s)</td>
              <td className="py-2 text-xs">{s.active ? 'Active' : 'Paused'}</td>
              <td className="py-2 text-right space-x-2">
                <button className="text-xs hover:underline" style={{ color: GREEN }} onClick={() => runNow(s)} data-testid={`schedule-run-${s.id}`}>Run now</button>
                <button className="text-xs hover:underline" style={{ color: '#6b7069' }} onClick={() => showRuns(s)}>Runs</button>
                <button className="text-xs hover:underline" style={{ color: '#6b7069' }} onClick={() => toggle(s)}>{s.active ? 'Pause' : 'Resume'}</button>
                <button className="text-xs hover:underline" style={{ color: '#B3401F' }} onClick={() => remove(s)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {runs && (
        <div className="mt-3 rounded-lg border p-3 text-xs" style={{ borderColor: '#E5E3DC' }} data-testid="schedule-runs">
          <div className="flex justify-between font-bold mb-2"><span>Run history</span>
            <button onClick={() => setRuns(null)} className="hover:underline" style={{ color: '#6b7069' }}>Close</button></div>
          {runs.list.length === 0 && <div style={{ color: '#9a9e96' }}>No runs yet</div>}
          {runs.list.map((r, i) => (
            <div key={`${r.run_at}-${i}`} className="border-t py-1.5" style={{ borderColor: '#F0EEE8' }}>
              {new Date(r.run_at).toLocaleString()} · <b>{r.status}</b> · sent: {r.sent_to.join(', ') || '—'}
              {r.skipped.length > 0 && <span style={{ color: '#B3401F' }}> · skipped: {r.skipped.map(s => `${s.user} (${s.reason})`).join('; ')}</span>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
